import { FormikProvider, useField, useFormik, useFormikContext, withFormik } from 'formik';
import { Box, Flex } from 'reflexbox';
import { useTranslation } from 'react-i18next';

import useLocalize from 'packages/kakapo-ui-application/hooks/useLocalize';
import {
	MomentInstance,
	MomentSkipReason,
	MomentTemplate,
	SkipMomentInstanceInput,
	SkipMomentTemplateInput,
	useSkipMomentInstanceMutation,
	useSkipMomentTemplateMutation,
} from 'generated/graphql';

import Paragraph from '../common/Paragraph';
import Modal, { ModalBackground, ModalContent } from '../modals/Modal';
import ModalHeader, { ModalHeaderTitle } from '../modals/ModalHeader';
import Select from '../forms/controls/Select';
import Label from '../forms/controls/Label';
import Field, { FieldType } from '../forms/controls/Field';
import PrimaryButton from '../buttons/PrimaryButton';
import { ButtonSize } from 'packages/kakapo-ui-kit/types/Button';
import PlainButton from '../buttons/PlainButton';
import Spinner from '../common/Spinner';
import GraphQLErrorMessage from '../forms/errors/GraphQLErrorMessage';
import { Fragment } from 'react';
import { Subheading } from '@treams/treams-ui';

export type CycleMomentInstance = {
	cycleInstanceID: string;
	instance?: MomentInstance;
	template: MomentTemplate;
};

type CycleMomentSkipModalProps = {
	moments: CycleMomentInstance[];
	onDismiss: () => void;
};

/**
 * Conditional type to determine if it's an moment instance or template
 * @template T - The type to conditionally extend.
 */
type InputType<T> = T extends MomentInstance ? SkipMomentInstanceInput : SkipMomentTemplateInput;

/**
 * Modal component for handling the skipping of one or more moment cycles
 * Generates reason selection boxes for every given moment
 */
export default ({ moments, onDismiss }: CycleMomentSkipModalProps) => {
	const { t } = useTranslation('', { keyPrefix: 'inbox.cycle.moments.skipModal' });
	const { localize } = useLocalize();

	const [commitInstance, { loading: loadingInstance, error: errorInstance }] =
		useSkipMomentInstanceMutation();
	const [commitTemplate, { loading: loadingTemplate, error: errorTemplate }] =
		useSkipMomentTemplateMutation();

	const initialValues = {
		moments: moments.map((moment, idx) => {
			return {
				index: idx,
				moment: moment,
				reason: undefined as MomentSkipReason | undefined,
				reasonOther: '',
			};
		}),
	};

	const formik = useFormik({
		initialValues,
		onSubmit: (values) => handleOnSubmit(values.moments),
	});

	const allMomentsValid = formik.values.moments.every(({ reason, reasonOther }) => {
		return reason !== MomentSkipReason.Other || reasonOther.length !== 0;
	});

	const isMultipleMoments = moments.length < 2;

	const headerTitle = isMultipleMoments
		? localize((moments[0].instance?.momentTemplate ?? moments[0].template).title).text
		: t('multipleMoments.title');

	const handleOnSubmit = (values: typeof initialValues.moments) => {
		if (values) {
			values.forEach(
				async ({ reason, reasonOther, moment: { instance, template, cycleInstanceID } }) => {
					const valid = reason && (reason !== MomentSkipReason.Other || reasonOther.length !== 0);

					if (!valid || loadingInstance || loadingTemplate) {
						return;
					}

					const baseInput = {
						skipReason: reason as MomentSkipReason,
						skipReasonOther: (reason === MomentSkipReason.Other && reasonOther) || undefined,
					};

					let input: InputType<typeof instance>;

					if (instance) {
						input = { id: instance.id, ...baseInput };

						commitInstance({ variables: { input } });
					} else {
						input = {
							cycleInstanceID,
							momentTemplateID: template.id,
							...baseInput,
						};

						commitTemplate({ variables: { input } });
					}
				},
			);
		}
		onDismiss();
	};

	return (
		<ModalBackground>
			<Modal>
				<ModalHeader onCancel={() => onDismiss()}>
					<ModalHeaderTitle>{headerTitle}</ModalHeaderTitle>
				</ModalHeader>
				<ModalContent>
					{loadingInstance || loadingTemplate ? (
						<Spinner isLoading size="18px" />
					) : (
						<FormikProvider value={formik}>
							{formik.values.moments.map((moment) => (
								<Fragment key={moment.moment.instance?.id}>
									{!isMultipleMoments && (
										<Subheading>
											{moment.moment.instance
												? localize(moment.moment.instance.momentTemplate.title).text
												: localize(moment.moment.template.title).text}
										</Subheading>
									)}
									<Paragraph color="slateBlue">{t('text')}</Paragraph>
									<Box mt={3}>
										<Label title={t('reason.label')} />
										<CycleMomentSkipReasonSelect moment={moment} />
									</Box>

									{moment.reason === MomentSkipReason.Other && (
										<Box mt={3}>
											<Label title={t('reasonOther.label')} />
											<Field
												name={`moments.${moment.index}.reasonOther`}
												type={FieldType.Text}
												fullWidth={true}
											/>
										</Box>
									)}
								</Fragment>
							))}
							<GraphQLErrorMessage error={(errorInstance ?? errorTemplate)?.graphQLErrors[0]} />
							<Flex justifyContent="space-between">
								<PlainButton size={ButtonSize.Medium} onClick={() => onDismiss()}>
									{t('cancelButton')}
								</PlainButton>
								<PrimaryButton
									size={ButtonSize.Medium}
									disabled={!allMomentsValid}
									onClick={() => formik.submitForm()}
								>
									{t('skipButton')}
								</PrimaryButton>
							</Flex>
						</FormikProvider>
					)}
				</ModalContent>
			</Modal>
		</ModalBackground>
	);
};

function CycleMomentSkipReasonSelect({ moment }) {
	const { t } = useTranslation('', { keyPrefix: 'inbox.cycle.moments.skipModal.reason' });
	const [{ value }] = useField<MomentSkipReason | undefined>(`reason-${moment.idx}`);
	const { setFieldValue } = useFormikContext();

	const options = [
		{
			label: t('duplicate'),
			value: MomentSkipReason.Duplicate,
		},
		{
			label: t('leave'),
			value: MomentSkipReason.Leave,
		},
		{
			label: t('illness'),
			value: MomentSkipReason.Illness,
		},
		{
			label: t('other'),
			value: MomentSkipReason.Other,
		},
	];

	return (
		<Select
			value={options.find((option) => option.value === value)}
			options={options}
			onChange={(option) => setFieldValue(`moments.${moment.index}.reason`, option!.value)}
		/>
	);
}
